import { API_BASE_URL } from '$lib/config';
import { getAccessToken, refreshSession, setAccessToken } from './auth';

export async function apiFetch<T>(
  endpoint: string,
  options: RequestInit = {},
  retried = false
): Promise<T> {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers as Record<string, string> | undefined)
    }
  });

  if (response.status === 401 && !retried && !endpoint.startsWith('/auth/')) {
    try {
      await refreshSession();
      return apiFetch<T>(endpoint, options, true);
    } catch {
      setAccessToken(null);
      throw new Error('Authentication required. Please log in again.');
    }
  }

  if (!response.ok) {
    const errorData: unknown = await response.json().catch(() => ({}));
    const detail =
      typeof errorData === 'object' && errorData !== null && 'detail' in errorData
        ? String((errorData as { detail?: unknown }).detail)
        : null;
    throw new Error(detail || `HTTP ${response.status}: ${response.statusText}`);
  }

  if (response.status === 204) return null as T;
  return (await response.json()) as T;
}

export function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error ?? 'An unexpected error occurred.');
}

// apiUpload posts a FormData body (multipart, e.g. a document upload) —
// deliberately not routed through apiFetch since that hardcodes a JSON
// Content-Type; the browser must set multipart's boundary itself.
export async function apiUpload<T>(endpoint: string, body: FormData, retried = false): Promise<T> {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    method: 'POST',
    body,
    headers: { Accept: 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) }
  });
  if (response.status === 401 && !retried) {
    try {
      await refreshSession();
      return apiUpload<T>(endpoint, body, true);
    } catch {
      setAccessToken(null);
      throw new Error('Authentication required. Please log in again.');
    }
  }
  if (!response.ok) {
    const errorData: unknown = await response.json().catch(() => ({}));
    const detail =
      typeof errorData === 'object' && errorData !== null && 'detail' in errorData
        ? String((errorData as { detail?: unknown }).detail)
        : null;
    throw new Error(detail || `HTTP ${response.status}: ${response.statusText}`);
  }
  return (await response.json()) as T;
}

// apiDownload fetches a file (with the auth header a plain <a href> can't
// carry) and triggers the browser's save-file flow via a throwaway object
// URL — used for the admin-only document download.
export async function apiDownload(endpoint: string, filename: string, retried = false): Promise<void> {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }
  });
  if (response.status === 401 && !retried) {
    await refreshSession();
    return apiDownload(endpoint, filename, true);
  }
  if (!response.ok) {
    const errorData: unknown = await response.json().catch(() => ({}));
    const detail =
      typeof errorData === 'object' && errorData !== null && 'detail' in errorData
        ? String((errorData as { detail?: unknown }).detail)
        : null;
    throw new Error(detail || `HTTP ${response.status}: ${response.statusText}`);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
